﻿import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import HeaderSection from '../components/Shared/HeaderSection'
import SegmentedControl from '../components/Shared/SegmentedControl'
import Kanban, { KANBAN_FILTER_FIELDS } from '../components/kanban/kanban'
import TableTickets from '../components/ticket/TableTickets'
import TicketSearchBar from '../components/ticket/TicketSearchBar'
import AuthAccessValidator from '../components/Auth/AuthAccessValidator'
import useTickets from '../hooks/useTickets'
import useTicketCatalogs from '../hooks/useTicketCatalogs'
import useTicketFilters from '../hooks/useTicketFilters'
import usePersistedState from '../hooks/usePersistedState'
import { UNASSIGNED } from '../utils/ticketTable'

/** Alcance de la lista: asignados a mí o todos. */
const SCOPE_OPTIONS = [
  { value: 'own', label: 'Míos', icon: 'fa-solid fa-user' },
  { value: 'all', label: 'Todos', icon: 'fa-solid fa-globe' },
]

/** Vistas disponibles (sólo ícono para ahorrar espacio). */
const VIEW_OPTIONS = [
  { value: 'kanban', icon: 'fa-brands fa-trello', title: 'Vista Kanban' },
  { value: 'table', icon: 'fa-solid fa-table-list', title: 'Vista tabla' },
]

/** En la tabla también se filtra por estado (en Kanban son las columnas). */
const TABLE_FILTER_FIELDS = ['statusId', ...KANBAN_FILTER_FIELDS]

/**
 * Gestión de tickets para soporte: una cabecera compacta y una sola barra con
 * búsqueda, filtros avanzados, alcance (míos/todos) y vista (Kanban/tabla).
 * La página filtra; el tablero y la tabla sólo muestran.
 */
const Tickets = () => {
  const navigate = useNavigate()
  const {
    tickets = [],
    loading,
    error,
    filter: scope,
    setFilter: setScope,
  } = useTickets()
  const [view, setView] = usePersistedState('tickets.view', 'kanban')
  const catalogs = useTicketCatalogs()
  const search = useTicketFilters(tickets, catalogs)

  const isTable = view === 'table'
  const unassignedOnly = search.filters.responsibleId === UNASSIGNED

  return (
    <AuthAccessValidator AccessNames={['ticket_support']} showError={true}>
      <div className="page-fill page-tickets-wrapper">
        <HeaderSection
          compact
          title="Gestión de Tickets"
          subtitle={
            scope === 'own'
              ? 'Tickets asignados a mí'
              : 'Supervisión y asignación de tareas'
          }
          icon="fa-solid fa-list-check"
          onButtonClick={() => navigate('/nuevo-ticket')}
          buttonText="Nuevo ticket"
          buttonClass="btn btn-save-ticket"
        />

        {/* BARRA ÚNICA: búsqueda + filtros + alcance + vista */}
        <div className="tk-toolbar mb-3">
          <TicketSearchBar
            filters={search.filters}
            onChange={search.setFilter}
            onClear={search.clearFilters}
            options={search.options}
            activeCount={search.activeCount}
            fields={isTable ? TABLE_FILTER_FIELDS : KANBAN_FILTER_FIELDS}
          >
            <span className="tk-count" aria-live="polite">
              {loading ? (
                <span className="spinner-border spinner-border-sm text-primary" />
              ) : (
                <>
                  <strong>{search.filtered.length}</strong>
                  {search.activeCount > 0 && ` de ${tickets.length}`}
                </>
              )}
            </span>
            <button
              type="button"
              className={`tk-quick ${unassignedOnly ? 'is-active' : ''}`}
              onClick={() =>
                search.setFilter(
                  'responsibleId',
                  unassignedOnly ? '' : UNASSIGNED
                )
              }
              aria-pressed={unassignedOnly}
              title="Mostrar sólo tickets sin encargado"
            >
              <i className="fa-solid fa-user-slash" />
              <span>Sin encargado</span>
            </button>
            <SegmentedControl
              ariaLabel="Alcance"
              options={SCOPE_OPTIONS}
              value={scope}
              onChange={setScope}
            />
            <SegmentedControl
              ariaLabel="Vista"
              options={VIEW_OPTIONS}
              value={view}
              onChange={setView}
            />
          </TicketSearchBar>
        </div>

        {error && (
          <div className="alert alert-danger border-0 small mb-3">
            <i className="fa-solid fa-triangle-exclamation me-2"></i>
            Error al cargar los tickets: {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={view}
            className="tk-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {isTable ? (
              <TableTickets
                items={search.filtered}
                totalItems={tickets.length}
              />
            ) : (
              <Kanban tickets={search.filtered} showSearch={false} />
            )}
          </motion.div>
        </AnimatePresence>

        <style>{`
          /* El contenido toma el alto restante: el Kanban scrollea dentro de
             sus columnas y la tabla dentro de su propio contenedor. */
          .tk-content { flex: 1 1 auto; min-height: 0; display: flex; flex-direction: column; }
          .tk-toolbar {
            flex-shrink: 0;
            padding: 8px;
            border: 1px solid var(--gm-border);
            border-radius: 12px;
            background: var(--gm-surface);
          }
          .tk-toolbar .tsb-search { background: var(--gm-surface-2); }
          .tk-count {
            min-width: 28px; font-size: 0.8rem; color: var(--gm-text-muted);
            font-variant-numeric: tabular-nums; text-align: right;
          }
          .tk-count strong { color: var(--gm-text); }
          .tk-quick {
            height: 34px; display: inline-flex; align-items: center; gap: 6px; padding: 0 10px;
            border-radius: 8px; border: 1px solid var(--gm-border); background: transparent;
            color: var(--gm-text-muted); font-size: 0.8rem; font-weight: 500;
          }
          .tk-quick:hover { color: var(--gm-text); }
          .tk-quick.is-active {
            border-color: var(--gm-primary); color: var(--gm-primary); background: var(--gm-primary-soft);
          }
          @media (max-width: 767px) {
            .tk-quick span { display: none; }
          }
          .btn-save-ticket {
            background: var(--gm-primary);
            color: #fff !important;
            font-weight: 500;
          }
          .btn-save-ticket:hover { background: var(--gm-primary-hover); }
        `}</style>
      </div>
    </AuthAccessValidator>
  )
}

export default Tickets
